import Debug from '../../helpers/debug.js';
import ora from 'ora';
import Table from 'cli-table';
import { pr, themed, printJSON } from '../../helpers/print.js';
import { killJob, getEnvJobs } from '../../helpers/api.js';

const debug = (...args) => {
  const debug = Debug('commands:jobs:bulkKill');
  debug(...args);
};

const command = 'bulk-kill';

const desc = 'Kill jobs in bulk';

const builder = async (yargs) => {
  debug('builder', command);

  return yargs
    .option('tenant-ids', {
      describe: 'Comma-separated list of tenant IDs to filter by',
      type: 'string',
      alias: ['tenants'],
    })
    .option('flow-ids', {
      describe: 'Comma-separated list of flow IDs to filter by',
      type: 'string',
      alias: ['flows'],
    })
    .option('reason', {
      describe: 'Reason for killing the jobs',
      type: 'string',
    });
};

const handler = async (argv) => {
  debug('handler', command, argv);

  const { hg, json, apikey, env, tenantIds, flowIds, reason } = argv;
  const { clientApiBaseUri } = hg;

  let message;
  let spinner = ora();
  try {
    // 1. Fetch jobs from API with status filter
    const statusFilter = 'JOB_CREATED,SYNC_STARTED,SYNC_SUCCESS,ETL_STARTED,ETL_SUCCESS,EXPORT_STARTED';
    message = themed(`Fetching running jobs...`);
    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));

    // Use a date range to get jobs from the last month
    const today = new Date();
    const oneMonthAgo = new Date(today);
    oneMonthAgo.setMonth(today.getMonth() - 1);
    const dateFrom = oneMonthAgo.toISOString().split('T')[0];

    const data = await getEnvJobs({
      debug,
      baseUri: clientApiBaseUri,
      env,
      apikey,
      from: dateFrom,
      status: statusFilter,
      count: 100, // Get max jobs per page
    });

    let jobs = data.jobs || [];
    const totalJobs = data.total || 0;

    !json && spinner.succeed(
      themed(`Found ${themed(totalJobs, 'info')} job(s) with specified status.`, 'secondary')
    );

    // 2. Filter by tenant IDs if provided
    if (tenantIds) {
      const tenantIdList = tenantIds.split(',').map(t => t.trim()).filter(t => t);
      if (tenantIdList.length > 0) {
        jobs = jobs.filter(job => {
          const jobTenant = job.tenant;
          return tenantIdList.includes(jobTenant);
        });
      }
    }

    // 3. Filter by flow IDs if provided
    if (flowIds) {
      const flowIdList = flowIds.split(',').map(f => f.trim()).filter(f => f);
      if (flowIdList.length > 0) {
        jobs = jobs.filter(job => {
          const jobFlow = job.flow_id;
          return flowIdList.includes(jobFlow);
        });
      }
    }

    if (jobs.length === 0) {
      json
        ? printJSON({ status: 'info', message: 'No jobs found matching the filters' })
        : spinner.warn(
            themed(`Warning: ${themed('No jobs found matching the specified filters')}.`, 'secondary')
          );
      return;
    }

    // 4. Kill all jobs in parallel
    message = themed(`Killing ${themed(jobs.length, 'info')} job(s)...`);
    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));

    const results = await Promise.all(
      jobs.map(async (job, index) => {
        const jobRoot = job.s3_root;
        const flow = job.flow_id;
        const tenant = job.tenant;

        if (!jobRoot) {
          return {
            index,
            flow,
            tenant,
            ok: false,
            error: {
              message: 'Job root (s3_root) not found in job data'
            },
          };
        }

        try {
          const response = await killJob({
            debug,
            baseUri: clientApiBaseUri,
            env,
            apikey,
            flow,
            user_id: tenant,
            job_root: jobRoot,
            reason,
          });

          return {
            index,
            flow,
            tenant,
            jobRoot,
            ok: true,
            response,
          };
        } catch (error) {
          let errorResult;
          
          if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            const statusCode = error.response.status;
            const statusText = error.response.statusText;
            const responseData = error.response.data;
            const message = error.message;
            
            debug('Error response:', {
              statusCode,
              statusText,
              message,
              data: responseData
            });
            
            errorResult = {
              statusCode,
              statusText,
              message,
              data: responseData
            };
          } else if (error.request) {
            // The request was made but no response was received
            debug('Error: No response received', error.message);
            errorResult = {
              message: error.message,
              request: error.request
            };
          } else {
            // Something happened in setting up the request that triggered an Error
            debug('Error:', error.message);
            errorResult = {
              message: error.message
            };
          }

          return {
            index,
            flow,
            tenant,
            jobRoot,
            ok: false,
            error: errorResult,
          };
        }
      })
    );

    !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));

    // 5. Print results
    if (json) {
      printJSON(results);
    } else {
      // Generate results table
      const table = new Table({
        head: ['Index', 'Flow', 'Tenant', 'Job Root', 'Status', 'Response/Error'],
      });

      results.forEach((result) => {
        const status = result.ok ? 'SUCCESS' : 'FAILED';
        const responseOrError = result.ok
          ? JSON.stringify(result.response, undefined, 2)
          : JSON.stringify(result.error, undefined, 2);
        table.push([
          result.index,
          result.flow || 'N/A',
          result.tenant || 'N/A',
          result.jobRoot || 'N/A',
          status,
          responseOrError,
        ]);
      });

      console.log(table.toString());

      // Print summary
      const successCount = results.filter((r) => r.ok).length;
      const failCount = results.filter((r) => !r.ok).length;
      pr(
        themed(
          `Summary: ${themed(String(successCount), 'info')} succeeded, ${themed(String(failCount), 'error')} failed out of ${themed(
            String(results.length),
            'info'
          )} total.`,
          'secondary'
        )
      );
    }
  } catch (err) {
    if (json) {
      printJSON({ status: 'error', error: err.message, stack: err.stack });
    } else {
      spinner.fail(themed(`Error: ${message || 'Failed to kill jobs'}.`, 'secondary'));
      pr(themed(`Message: ${themed(err.message)}`, 'secondary'));
      debug(err);
    }
    process.exit(1);
  }
};

export { command, desc, builder, handler };

